import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCorrelationDashboard, runCorrelationNow } from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const CHAIN_COLORS = {
  detected: 'bg-red-500',
  clear: 'bg-gray-200',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'text-red-700 bg-red-50' : pct >= 60 ? 'text-orange-700 bg-orange-50' : 'text-yellow-700 bg-yellow-50';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>{pct}%</span>;
}

export default function CorrelationDashboardPage() {
  const navigate = useNavigate();
  const { data, loading, refresh } = useCorrelationDashboard();
  const [running, setRunning] = useState(false);

  const dashboard = data?.data;
  const correlations = dashboard?.active_correlations ?? [];
  const killChain = dashboard?.kill_chain;
  const stats = dashboard?.stats;

  async function handleRunNow() {
    setRunning(true);
    try {
      await runCorrelationNow();
      refresh();
    } catch { /* handled by UI */ } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-lg" />)}</div>
          <div className="h-32 bg-gray-200 rounded-lg" />
          <div className="h-96 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Correlation Engine</h1>
          <p className="text-sm text-gray-500 mt-1">Cross-source attack chain detection &middot; 8 correlation rules</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRunNow} disabled={running}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
            {running ? 'Running...' : 'Run Now'}
          </button>
          <button onClick={() => navigate('/secops')}
            className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            Command Center
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-3xl font-bold text-gray-900">{stats?.total_active ?? 0}</p>
          <p className="text-sm text-gray-500 mt-1">Active Correlations</p>
          <div className="mt-3 h-1 w-12 rounded bg-indigo-500" />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-3xl font-bold text-gray-900">{stats?.critical ?? 0}</p>
          <p className="text-sm text-gray-500 mt-1">Critical Severity</p>
          <div className="mt-3 h-1 w-12 rounded bg-red-500" />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-3xl font-bold text-gray-900">{stats?.avg_confidence ? `${Math.round(stats.avg_confidence * 100)}%` : '—'}</p>
          <p className="text-sm text-gray-500 mt-1">Avg Confidence</p>
          <div className="mt-3 h-1 w-12 rounded bg-yellow-500" />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-3xl font-bold text-gray-900">{killChain?.coverage_percentage ?? 0}%</p>
          <p className="text-sm text-gray-500 mt-1">Kill Chain Coverage</p>
          <div className="mt-3 h-1 w-12 rounded bg-purple-500" />
        </div>
      </div>

      {/* Kill Chain Visualization */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Kill Chain Visualization</h2>
        <p className="text-xs text-gray-500 mb-4">{killChain?.total_detected ?? 0} of {killChain?.stages?.length ?? 12} phases detected</p>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {(killChain?.stages ?? []).map((stage, i) => (
            <div key={stage.phase} className="flex items-center">
              <div className={`relative flex flex-col items-center min-w-[80px]`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
                  stage.detected ? 'bg-red-500 text-white ring-2 ring-red-300' : 'bg-gray-200 text-gray-500'
                }`}>
                  {stage.incident_count || '—'}
                </div>
                <p className={`text-[10px] mt-1.5 text-center leading-tight ${stage.detected ? 'text-red-700 font-medium' : 'text-gray-400'}`}>
                  {stage.label}
                </p>
                <p className="text-[9px] text-gray-400">{stage.mitre_tactic}</p>
                {stage.rules_matched.length > 0 && (
                  <p className="text-[9px] text-red-500 mt-0.5">{stage.rules_matched.join(', ')}</p>
                )}
              </div>
              {i < (killChain?.stages?.length ?? 0) - 1 && (
                <div className={`h-0.5 w-4 ${stage.detected ? CHAIN_COLORS.detected : CHAIN_COLORS.clear}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Source breakdown */}
      {stats?.by_source && Object.keys(stats.by_source).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="text-gray-500 font-medium">Sources:</span>
            {Object.entries(stats.by_source).map(([src, count]) => (
              <span key={src} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-indigo-400" />
                {src.replace(/_/g, ' ')}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Active Correlations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Active Correlations</h2>
          <span className="text-xs text-gray-400">Updated {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleTimeString() : '—'}</span>
        </div>
        {correlations.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">
            No active correlations. The engine evaluates 8 rules every 5 minutes across endpoint, M365, and network sources.
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Correlation</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Rule</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Confidence</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Sources</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">MITRE</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {correlations.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/secops/incidents/${c.id}`)}>
                  <td className="px-6 py-3">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{c.title}</p>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{c.description}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 font-mono">{c.correlation_rule_id}</td>
                  <td className="px-4 py-3"><Badge value={c.severity} colors={SEVERITY_COLORS} /></td>
                  <td className="px-4 py-3"><ConfidenceBadge value={c.correlation_confidence} /></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.correlation_sources.map(s => (
                        <span key={s} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.mitre_techniques.slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-mono">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Correlation Rules Reference */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Correlation Rules</h2>
          <p className="text-xs text-gray-500 mt-0.5">8 cross-source rules evaluated every 5 minutes</p>
        </div>
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Rule</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Kill Chain</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">MITRE</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Hits</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(dashboard?.rules ?? []).map(rule => (
              <tr key={rule.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-sm font-mono text-indigo-600">{rule.id}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{rule.name}</td>
                <td className="px-4 py-3"><Badge value={rule.severity} colors={SEVERITY_COLORS} /></td>
                <td className="px-4 py-3 text-xs text-gray-600 capitalize">{rule.kill_chain_phase.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {rule.mitre_techniques.map(t => (
                      <span key={t} className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-mono">{t}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">
                  {stats?.by_rule?.[rule.id] ?? 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
